import { useAuth } from '@/hooks/useAuth'
import { useCurrentInstitute } from '@/hooks/useCurrentInstitute'
import { RoleContextValue, UserRole } from '@/types'
import { createContext, useCallback, useContext, useMemo } from 'react'
import { getHighestRole, getScopedInstitutes } from '@/utils/instituteRoles'

/**
 * Role and institute membership flags derived from the authenticated user's
 * custom claims. Returns booleans for role checks and the active institute scope.
 */

export const RoleContext = createContext<RoleContextValue>({} as RoleContextValue);


/**
 * Provider that exposes effective role and institute selection state.
 * Computes selected institute scope from claims, derives manager/tutor/student
 * flags, and exposes ownership helpers consumed by feature modules.
 */
export function RoleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const instituteRoles = user?.instituteRoles ?? {}
  const { currentInstituteId, setCurrentInstituteId, selectableInstitutes, canSelectGlobal } =
    useCurrentInstitute()

  const selectedInstituteRole = currentInstituteId
    ? instituteRoles[currentInstituteId]
    : getHighestRole(instituteRoles)
  const isSuperUser = user?.role === 'superuser' || selectedInstituteRole === 'superuser'
  const role: UserRole | undefined = isSuperUser
    ? 'superuser'
    : (selectedInstituteRole ?? user?.role ?? undefined)
  const institutes = getScopedInstitutes(instituteRoles, currentInstituteId, isSuperUser)
  const isOwned = useCallback((ownerId: string) =>
    (ownerId === 'global' ? isSuperUser : ((institutes || [])?.includes(ownerId) ?? false)), [institutes, isSuperUser]
  )
  const value: RoleContextValue =  useMemo(() =>  ({
    uid: user?.uid as string | undefined,
    role,
    isSuperUser,
    isManager: role === 'manager',
    isTutor: role === 'tutor',
    isStudent: role === 'student',
    /** Manager or Tutor can create/edit educational content in their institute */
    canManageContent: isSuperUser || role === 'manager' || role === 'tutor',
    /** Manager or SuperUser can manage institute members */
    canManageInstitute: isSuperUser || role === 'manager',
    isAuthenticated: user !== null,
    /** Active institute scope derived from instituteRoles. */
    institutes,
    instituteRoles,
    currentInstituteId,
    setCurrentInstituteId,
    selectableInstitutes,
    canSelectGlobal,
    hasGlobalScope: isSuperUser && (currentInstituteId === 'global' || currentInstituteId === null),
    isOwned
  }), [canSelectGlobal, currentInstituteId, institutes, instituteRoles, isOwned, isSuperUser, role, selectableInstitutes, user])
  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>
}

/** Returns the current RoleContext value. Must be used inside RoleProvider. */
export function useRoleContext() {
  return useContext(RoleContext);
}
